import { Bug, Lightbulb, Maximize2, Minimize2, Send, Sparkles, Trash2, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import srIcon from '@/assets/loans-icon.svg';
import { type ChatMessage, useAiChat } from '@/hooks/use-ai-chat';
import { cn } from '@/lib/cn';

interface Props {
  loanId?: string;
  loanNumber?: string;
}

export function AiChatPanel({ loanId, loanNumber }: Props) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [input, setInput] = useState('');
  const { messages, sendMessage, isLoading, clearMessages } = useAiChat(loanId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const inputPlaceholder = loanId
    ? `Ask about ${loanNumber || 'this loan'}…`
    : 'Ask about your portfolio…';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    el.style.height = 'auto';
    const computed = window.getComputedStyle(el);
    const lineHeight = Number.parseFloat(computed.lineHeight || '20');
    const verticalPadding =
      Number.parseFloat(computed.paddingTop || '0') +
      Number.parseFloat(computed.paddingBottom || '0');
    const borderSize =
      Number.parseFloat(computed.borderTopWidth || '0') +
      Number.parseFloat(computed.borderBottomWidth || '0');
    const maxHeight = lineHeight * 2 + verticalPadding + borderSize;
    const nextHeight = Math.min(el.scrollHeight, maxHeight);

    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [input, inputPlaceholder]);

  function handleSend() {
    const msg = input.trim();
    if (!msg || isLoading) return;
    setInput('');
    sendMessage(msg);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // Quick prompts when no messages
  const quickPrompts = loanId
    ? [
        'Summarise this loan',
        'What are the key risks?',
        'How does the return compare to our portfolio average?',
        'What conditions should we consider for approval?',
      ]
    : [
        'Which loans are maturing this month?',
        'What is our total book exposure?',
        'Show me the top 5 delinquent loans',
        'How is our portfolio performing?',
      ];

  if (!open) {
    return (
      <Button
        variant="primary"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 text-white rounded-full shadow-xl hover: transition-all hover:scale-105"
        title="Open AI Assistant"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-medium">AI Assistant</span>
      </Button>
    );
  }

  return (
    <div
      className={cn(
        'fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col transition-all',
        expanded ? 'bottom-4 right-4 left-4 top-20' : 'bottom-6 right-6 w-[420px] h-[560px]',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-900 rounded-t-xl">
        {/* bg-gradient-to-r  from-gray-900 to-gray-700 */}
        <div className="relative flex items-center gap-2">
          <img src={srIcon} alt="CompliOne Loans" className="h-4 w-4 shrink-0" />
          <h3 className="text-sm font-semibold text-white">Loans AI</h3>
          {loanNumber && (
            <span className="text-[10px] bg-gray-500 text-gray-100 px-2 py-0.5 rounded-full">
              {loanNumber}
            </span>
          )}
          <span className="text-[9px] text-gray-300 font-mono">Gemini 3 Flash</span>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <Button
              variant="plain"
              onClick={clearMessages}
              className="min-w-0  rounded-md bg-gray-900 bg-opacity-70 border-gray-900  text-gray-200 border hover:border-gray-700"
              title="Clear chat"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="plain"
            onClick={() => setExpanded(!expanded)}
            className="min-w-0 rounded-md bg-gray-900 bg-opacity-70 border-gray-900  text-gray-200 border  hover:border-gray-700"
          >
            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button
            variant="plain"
            onClick={() => setOpen(false)}
            className="min-w-0 rounded-md bg-gray-900 bg-opacity-70 border-gray-900  text-gray-200 border  hover:border-gray-700"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <Sparkles className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 mb-4">
              {loanId
                ? `Ask me anything about ${loanNumber || 'this loan'}…`
                : 'Ask me anything about your loan portfolio…'}
            </p>
            <div className="space-y-2">
              {quickPrompts.map(prompt => (
                <Button
                  variant="secondary"
                  key={prompt}
                  onClick={() => {
                    setInput(prompt);
                    sendMessage(prompt);
                  }}
                  className="block w-full text-left text-xs text-gray-600 hover: px-3 py-2 rounded-lg transition-colors"
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={i} message={msg} />
        ))}

        {isLoading && (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Spinner size="lg" className="w-4 h-4 animate-spin" />
            <span>Thinking…</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={inputPlaceholder}
            rows={1}
            className="flex-1 min-w-0 max-h-[4.5rem] resize-none overflow-y-hidden px-3 py-2 text-sm leading-5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={isLoading}
          />
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="w-fit min-w-10 p-2 text-white rounded-lg hover: disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-3 mt-1.5">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('app:open-feature-request'))}
            className="text-[10px] text-gray-600 hover:text-gray-400 flex items-center gap-1"
          >
            <Lightbulb className="w-3 h-3" />
            Request a feature
          </button>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('app:open-bug-report'))}
            className="text-[10px] text-red-400 hover:text-red-600 flex items-center gap-1"
          >
            <Bug className="w-3 h-3" />
            Report a bug
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm',
          isUser
            ? 'bg-gray-600 text-white rounded-br-sm'
            : 'bg-gray-100 text-gray-800 rounded-bl-sm',
        )}
      >
        <div className="whitespace-pre-wrap break-words leading-relaxed">{message.content}</div>
        {message.createdAt && (
          <p className={cn('text-[9px] mt-1', isUser ? 'text-gray-200' : 'text-gray-400')}>
            {new Date(message.createdAt).toLocaleTimeString('en-AU', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        )}
      </div>
    </div>
  );
}
